// ==================== ABOUT US PAGE COMPONENT ====================

import React, { useEffect, useState } from "react";
import Footer from "./Footer.js";
import Headerpage from "./Header2.js";
import { getData, serverURL } from "../../../Services/Fetchnodeservices.js";
import {
  Shield,
  Truck,
  Headphones,
  ShoppingBag,
  Zap,
  Clock,
  Star,
  Users,
  Award,
  Globe,
  CheckCircle,
  FileText,
  Calendar,
  TrendingUp,
  Heart,
} from "lucide-react";

const AboutUs = ({ isDark }) => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        accent: "#8b5cf6",
        accentLight: "#a78bfa",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        accent: "#8b5cf6",
        accentLight: "#c4b5fd",
      };

  const fetchAboutUs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("aboutus/aboutus");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setAboutData(response.data);
      } else {
        setError("About Us page not found");
      }
    } catch (err) {
      console.error("Error fetching about us:", err);
      setError("Failed to load About Us page. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAboutUs();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: `3px solid ${t.border}`,
                borderTopColor: t.accent,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: t.textSecondary }}>Loading About Us...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "20px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              backgroundColor: t.cardBg,
              padding: "40px",
              borderRadius: "20px",
              border: `1px solid ${t.border}`,
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <Shield size={40} color="#ef4444" />
            </div>
            <h2 style={{ color: t.heading, marginBottom: "12px" }}>
              About Us Page Not Found
            </h2>
            <p style={{ color: t.textSecondary, marginBottom: "24px" }}>
              {error}
            </p>
            <button
              onClick={fetchAboutUs}
              style={{
                padding: "12px 24px",
                backgroundColor: t.accent,
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .about-content {
          word-wrap: break-word;
        }
        .about-content h1 {
          font-size: 2rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 700;
        }
        .about-content h2 {
          font-size: 1.5rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
        }
        .about-content h3 {
          font-size: 1.25rem;
          margin-top: 1.2em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
        }
        .about-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .about-content ul,
        .about-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .about-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
        }
        .about-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .about-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .about-content h1 {
            font-size: 1.5rem;
          }
          .about-content h2 {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />

        {/* Hero Section */}
        <section
          style={{
            position: "relative",
            minHeight: "350px",
            padding: "60px 20px",
            textAlign: "center",
            background: `linear-gradient(135deg, #0b0417 0%, #160a2e 40%, ${t.accent} 100%)`,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              background: "rgba(139,92,246,0.15)",
              filter: "blur(60px)",
              borderRadius: "50%",
              top: "-150px",
              left: "-100px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "250px",
              height: "250px",
              background: "rgba(139,92,246,0.12)",
              filter: "blur(50px)",
              borderRadius: "50%",
              bottom: "-100px",
              right: "-80px",
            }}
          />

          <div
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "800px",
            }}
          >
            {aboutData?.page_thumbnail && (
              <div style={{ marginBottom: "24px" }}>
                <img
                  src={`${serverURL}/images/${aboutData.page_thumbnail}`}
                  alt={aboutData.page_title}
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid white",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            <h1
              style={{
                fontSize: "clamp(36px, 6vw, 56px)",
                fontWeight: "700",
                marginBottom: "16px",
              }}
            >
              {aboutData?.page_title || "About Us"}
            </h1>

            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "14px",
                opacity: 0.9,
              }}
            >
              <Clock size={18} />
              <span>Last Updated: {formatDate(aboutData?.updated_at)}</span>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section
          style={{
            padding: "60px 20px",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "1100px",
              margin: "0 auto",
            }}
          >
            {/* Stats Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "24px",
                marginBottom: "48px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <Users size={28} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "28px", marginBottom: "4px" }}>50+</h3>
                <p style={{ color: t.textSecondary, fontSize: "13px" }}>Team Members</p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <ShoppingBag size={28} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "28px", marginBottom: "4px" }}>10k+</h3>
                <p style={{ color: t.textSecondary, fontSize: "13px" }}>Happy Customers</p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <Truck size={28} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "28px", marginBottom: "4px" }}>500+</h3>
                <p style={{ color: t.textSecondary, fontSize: "13px" }}>Daily Deliveries</p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "24px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <Star size={28} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "28px", marginBottom: "4px" }}>4.8</h3>
                <p style={{ color: t.textSecondary, fontSize: "13px" }}>Customer Rating</p>
              </div>
            </div>

            {/* Main Content Card */}
            <div
              style={{
                backgroundColor: t.cardBg,
                borderRadius: "28px",
                border: `1px solid ${t.border}`,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ padding: "48px" }}>
                <div
                  className="about-content"
                  dangerouslySetInnerHTML={{
                    __html: aboutData?.page_description || "No content available",
                  }}
                />

                {/* Footer Info */}
                <div
                  style={{
                    marginTop: "48px",
                    paddingTop: "24px",
                    borderTop: `1px solid ${t.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                    fontSize: "13px",
                    color: t.textSecondary,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileText size={16} />
                    <span>Page ID: {aboutData?.id}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Calendar size={16} />
                    <span>Created: {formatDate(aboutData?.created_at)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle size={16} />
                    <span>Status: {aboutData?.status === 1 ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Values Section */}
            <div
              style={{
                marginTop: "48px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
              }}
            >
              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Heart size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Customer First
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  We prioritize our customers' needs and satisfaction above everything else
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Zap size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Fast Delivery
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  Lightning-fast delivery to ensure you get your orders on time
                </p>
              </div>

              <div
                style={{
                  background: t.cardBg,
                  padding: "28px",
                  borderRadius: "20px",
                  border: `1px solid ${t.border}`,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${t.accent}15`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <Shield size={32} color={t.accent} />
                </div>
                <h3 style={{ color: t.heading, fontSize: "18px", marginBottom: "12px" }}>
                  Quality Assurance
                </h3>
                <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                  We ensure the highest quality standards for all our products
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div
              style={{
                background: `linear-gradient(135deg, ${t.accent}15, ${t.accent}08)`,
                borderRadius: "24px",
                padding: "48px",
                marginTop: "48px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <h3
                style={{
                  color: t.heading,
                  fontSize: "28px",
                  marginBottom: "16px",
                }}
              >
                Ready to Experience Zaptodoor?
              </h3>
              <p
                style={{
                  color: t.textSecondary,
                  marginBottom: "28px",
                  fontSize: "16px",
                  maxWidth: "600px",
                  margin: "0 auto 28px",
                }}
              >
                Join thousands of happy customers who trust Zaptodoor for their daily needs
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="/contact"
                  style={{
                    background: t.accent,
                    color: "white",
                    padding: "14px 32px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                  }}
                >
                  <Headphones size={18} />
                  Contact Us
                </a>
                <a
                  href="/shop"
                  style={{
                    background: "transparent",
                    color: t.text,
                    padding: "14px 32px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${t.border}`,
                    fontWeight: "500",
                  }}
                >
                  <ShoppingBag size={18} />
                  Start Shopping
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;